// Pick the right paid-report CTA to surface after the free kundali result.
// Inputs in priority order:
//   1. ?next_report=<plan_id>  — user arrived from /reports/<plan>/ PDP; honour their choice
//   2. Strongest active dosha  — Pitra/Sade Sati route to their specific report;
//                                other dosha cases route to the general remedy plan
//   3. Default                 — brihat_kundali (highest-ticket headline read)
//
// Mangal NOTE: we deliberately do NOT route Mangal -> marriage_report from this
// surface. marriage_report needs both person_a and person_b birth details
// (it is the Kundali Milan output). Users hitting this CTA after a single-person
// kundali have only their own DOB, so the order created but never paid (13%
// create->paid vs 28% on other plans — trailing 14d). Marriage Report stays
// gated behind /kundli-matching and /gun-milan where partner data exists.
//
// Centralised here so KundaliResult, the chat panel, and any future surface
// pick the same plan + price + copy. Prices come from @ishvaram/domain-data,
// generated from canonical commerce/consultation_plans.yaml.

import { primaryOfferForIntent } from "@ishvaram/domain-data/intent-offer";
import type { KundaliResponse } from "@/lib/api/kundali";
import type { RecommendResult } from "@/lib/recommendation-shadow";
import { safeSessionGet } from "@/lib/browser-storage";
import { GOVERNED_OFFER_INTENT, intentFromChatText } from "@/lib/customer-intent";
import { getSEOIntentConfigForSource, PLAN_ID, type SEOReportPlanId } from "@/lib/seo-intents";
import {
  copyFor,
  PLAN_DEFAULTS,
  chatReadingValueCopy,
  painFirstVariantCopy,
  secondaryPlanCopy,
  type PrimaryReportReason,
} from "@/lib/post-kundali-cta-data";

// The copy + plan-default data lives in the sibling ./post-kundali-cta-data
// module (split out 2026-06-23 to keep this file under the changed-executable
// LOC limit, guard G03). Re-export the public copy entry points so external
// imports of `secondaryPlanCopy` / `painFirstVariantCopy` from
// "@/lib/post-kundali-cta" keep resolving unchanged.
export { painFirstVariantCopy, secondaryPlanCopy };
export { chatReadingValueCopy };

// Page categories whose SEO entry surface carries a DEDICATED intent-matched
// report (career → career_report, marriage → marriage_report) that must override
// the dosha pick (rule 40 never_bury). A generic chart/kundali entry resolves to
// the headline brihat plan — that must NOT suppress dosha personalisation, so
// only these categories count as an intent override; everything else falls
// through to the dosha/default branches.
const DEDICATED_REPORT_PAGE_CATEGORIES = new Set<string>(["career", "marriage"]);

/**
 * Resolve the seeker's intent-matched report plan from an SEO entry/source PATH
 * (the live URL, or the `seo_entry_path` stored at chart generation).
 * `getSEOIntentConfigForSource` applies the slug-pattern overrides (career →
 * career_report, marriage → marriage_report) that the base intent config drops.
 * Returns undefined when the path is absent or NOT a dedicated-report surface, so
 * the caller falls through to the dosha/default pick. This is the single source
 * for "which report did the seeker's surface intend?" — both the inline
 * kundali-result and the dedicated chat-reading offer call it, so the career
 * never_bury fix can't drift between the two surfaces (rules 23/24).
 */
export function intentReportPlanIdFromPath(sourcePath?: string | null): string | undefined {
  if (!sourcePath) return undefined;
  const config = getSEOIntentConfigForSource(sourcePath);
  if (!DEDICATED_REPORT_PAGE_CATEGORIES.has(config.pageCategory)) return undefined;
  return config.reportPlanId;
}

/**
 * THE single browser-side resolver for the seeker's entry-intent report plan —
 * every value surface (inline/saved kundali result, /chat/[readingId]) calls
 * this one function so the resolution can never drift between them (rules
 * 23/24). Resolution order:
 *   1. the STORED entry path (`seo_entry_path`, written at SEO entry / by a
 *      dedicated-tool page) — it survives navigation to /chat/[readingId] and
 *      /kundali/[readingId], where the live pathname carries no intent;
 *   2. the LIVE pathname — makes a dedicated-intent page (e.g. the
 *      /career-astrology tool, whose inline result renders on the career page
 *      itself) correct BY CONSTRUCTION even when sessionStorage is blocked or
 *      was never written (the 2026-07-02 under-recovery class: the store was
 *      only written on some CTA click paths, so most career charts lost their
 *      intent downstream).
 * A stored path that is NOT a dedicated surface falls through to the live
 * pathname (the seeker's current explicit surface wins over a stale generic
 * entry). marriage_report stays safe downstream via the allowMarriageReport
 * gate in pickPrimaryReport.
 */
export function resolveEntryIntentReportPlanId(): string | undefined {
  if (typeof window === "undefined") return undefined;
  let storedPath: string | null = null;
  try {
    storedPath = safeSessionGet("seo_entry_path");
  } catch {
    storedPath = null;
  }
  return (
    intentReportPlanIdFromPath(storedPath) ?? intentReportPlanIdFromPath(window.location.pathname)
  );
}

/**
 * The report plan matched to the concern the seeker EXPRESSED IN THE
 * CONVERSATION — the chat_concern_match_pilot_2026_07 treatment signal.
 * REUSES the two governed mappings that already exist: `intentFromChatText`
 * (scans lines last-first, so the latest expressed concern wins) and the
 * rule-40 intent→offer policy (`GOVERNED_OFFER_INTENT` → `primaryOfferForIntent`
 * from intent_offer_alignment.yaml) — no parallel concern→plan map to drift
 * (rules 23/24/40). Returns null when no concern is expressed
 * (unknown/low_intent/chat_now) or the governed primary is not a report plan
 * (chat → the ₹29 quick session, which belongs to the paywall, not this card).
 */
export function concernMatchedReportPlanId(chatText: string | undefined): string | null {
  if (!chatText?.trim()) return null;
  const intent = intentFromChatText(chatText);
  if (intent === "unknown" || intent === "low_intent" || intent === "chat_now") return null;
  const planId = primaryOfferForIntent(GOVERNED_OFFER_INTENT[intent]);
  if (!planId || !VALID_PLAN_IDS.has(planId as SEOReportPlanId)) return null;
  return planId;
}

type KundaliDosha = KundaliResponse["doshas"][number];

export interface PrimaryReportPick {
  planId: SEOReportPlanId;
  priceInr: number;
  /** Exact local charge returned by /plans for this seeker (ADR-0048).
   * Added by the shared OfferPlacement pricing hook; absent means INR. */
  checkoutAmountMinor?: number;
  checkoutCurrency?: string;
  strikePriceInr?: number;
  headline: string;
  subhead: string;
  reasonChip: string;
  /** Why this plan was picked — surfaces in analytics for routing rank attribution. */
  reason: PrimaryReportReason;
}

const VALID_PLAN_IDS = new Set<SEOReportPlanId>(Object.values(PLAN_ID));

const DOSHA_RULES: {
  matches: string[];
  planId: SEOReportPlanId;
  reason: PrimaryReportReason;
}[] = [
  { matches: ["mangal"], planId: PLAN_ID.dosha_remedy_plan, reason: "dosha_mangal" },
  { matches: ["pitra"], planId: PLAN_ID.pitra_dosh, reason: "dosha_pitra" },
  { matches: ["sade", "shani"], planId: PLAN_ID.sade_sati, reason: "dosha_sade_sati" },
  { matches: ["kaal", "sarp"], planId: PLAN_ID.dosha_remedy_plan, reason: "dosha_kaal_sarp" },
];

function normalizeNextReport(raw: string | null | undefined): SEOReportPlanId | null {
  if (!raw) return null;
  const candidate = raw.trim() as SEOReportPlanId;
  return VALID_PLAN_IDS.has(candidate) ? candidate : null;
}

function topActiveDosha(doshas: KundaliDosha[]) {
  return [...doshas].filter((d) => d.is_present).sort((a, b) => b.severity - a.severity)[0];
}

function buildPick(
  planId: SEOReportPlanId,
  reason: PrimaryReportPick["reason"],
  doshaName?: string,
  language?: string
): PrimaryReportPick {
  return {
    planId,
    ...PLAN_DEFAULTS[planId],
    ...copyFor(planId, reason, doshaName, language),
    reason,
  };
}

function doshaRuleFor(doshaId: string) {
  return DOSHA_RULES.find((rule) => rule.matches.some((token) => doshaId.includes(token)));
}

function requestedReportPick({
  requested,
  allowMarriageReport,
  language,
}: {
  requested: SEOReportPlanId | null;
  allowMarriageReport: boolean;
  language: string;
}): PrimaryReportPick | null {
  if (!requested) return null;
  if (requested === PLAN_ID.marriage_report && !allowMarriageReport) {
    return buildPick(PLAN_ID.brihat_kundali, "default_brihat", undefined, language);
  }
  return buildPick(requested, "next_report_param", undefined, language);
}

export function pickPrimaryReport({
  doshas = [],
  nextReportParam,
  intentReportPlanId,
  concernReportPlanId,
  allowMarriageReport = false,
  language = "en",
}: {
  doshas?: KundaliDosha[];
  nextReportParam?: string | null;
  /**
   * The intent-matched report plan derived from the seeker's SEO entry surface
   * (e.g. a career-astrology page → career_report). It is the rule-40 never_bury
   * signal: a seeker who arrived with a dedicated-report intent must be offered
   * THAT report, not buried under the generic dosha override. It sits ABOVE the
   * dosha rules in priority because the seeker's stated intent outranks an
   * incidental dosha most charts happen to carry. It sits BELOW an explicit
   * `next_report` deep-link (an even more deliberate choice).
   *
   * Why this exists (live Razorpay, 2026-06): #844 moved the value moment to the
   * dedicated /chat/[readingId] surface whose offer (pickChatReadingReportOffer →
   * this fn) is dosha-driven with NO intent input, so career seekers — career is
   * not a dosha — were always routed to brihat/dosha reports. career_report sales
   * went to ₹0 from 2026-06-09 while every other report (driven by its own
   * landing page or a dosha rule) kept selling. Threading the entry intent here
   * restores career as a first-class offer on the chat surface.
   */
  intentReportPlanId?: string | null;
  /**
   * The plan matched to the concern the seeker expressed IN the conversation
   * (`concernMatchedReportPlanId`). Callers pass it ONLY for the
   * chat_concern_match_pilot_2026_07 treatment arm — control stays
   * byte-identical by never supplying it. It sits BELOW the explicit
   * `next_report` deep-link (a deliberate report choice always wins) and ABOVE
   * the entry intent: what the seeker just SAID in the chat is a fresher
   * signal than the page they arrived from, and it must not regress the
   * 2026-06-09 entry-intent ordering for control (that chain is untouched).
   */
  concernReportPlanId?: string | null;
  allowMarriageReport?: boolean;
  language?: string;
}): PrimaryReportPick {
  // 1. Explicit deep-link from /reports/<plan>/ wins outright.
  const requested = normalizeNextReport(nextReportParam);
  const requestedPick = requestedReportPick({ requested, allowMarriageReport, language });
  if (requestedPick) return requestedPick;

  // 2./3. Conversation-expressed concern (treatment arm only — see the param
  //    doc), then the SEO entry intent (rule 40 never_bury) — both outrank the
  //    dosha override below and share the marriage-needs-partner-data guard.
  const signalPick = signalReportPick({
    concernReportPlanId,
    intentReportPlanId,
    allowMarriageReport,
    language,
  });
  if (signalPick) return signalPick;

  // 4. Dosha-driven personalisation.
  const top = topActiveDosha(doshas);
  if (top) {
    const rule = doshaRuleFor(top.dosha_id?.toLowerCase() ?? "");
    if (rule) return buildPick(rule.planId, rule.reason, top.name_en, language);
  }

  // 5. Default — Brihat is the headline read.
  return buildPick(PLAN_ID.brihat_kundali, "default_brihat", undefined, language);
}

/**
 * The two expressed-signal picks between the deep-link and the dosha chain:
 * the conversation-expressed concern (chat_concern_match_pilot_2026_07
 * treatment) first, then the SEO entry intent. Fresher signal wins — what the
 * seeker just SAID outranks the page they arrived from.
 */
function signalReportPick({
  concernReportPlanId,
  intentReportPlanId,
  allowMarriageReport,
  language,
}: {
  concernReportPlanId: string | null | undefined;
  intentReportPlanId: string | null | undefined;
  allowMarriageReport: boolean;
  language: string;
}): PrimaryReportPick | null {
  return (
    intentReportPick({
      intentReportPlanId: concernReportPlanId,
      allowMarriageReport,
      language,
      reason: "chat_concern",
    }) ?? intentReportPick({ intentReportPlanId, allowMarriageReport, language })
  );
}

/**
 * An intent-matched report from the entry surface, honoured with the same
 * marriage-needs-partner-data guard as an explicit request. Returns null when no
 * intent plan was supplied or it isn't a known report plan (so the caller falls
 * through to dosha/default). marriage_report without partner data is intentionally
 * dropped (NOT substituted) — there is no useful single-person marriage offer, so
 * the dosha/default branches give a better chart-matched pick.
 */
function intentReportPick({
  intentReportPlanId,
  allowMarriageReport,
  language,
  reason = "entry_intent",
}: {
  intentReportPlanId: string | null | undefined;
  allowMarriageReport: boolean;
  language: string;
  /** "entry_intent" (SEO surface) or "chat_concern" (conversation) — distinct
   * reasons (NOT next_report_param) so route_rank_reason attributes each
   * recovery in events_raw, and the checkout-recovery auto-open in
   * KundaliResult stays tied to genuine ?next_report deep-links only. */
  reason?: PrimaryReportReason;
}): PrimaryReportPick | null {
  if (!intentReportPlanId) return null;
  const candidate = intentReportPlanId.trim() as SEOReportPlanId;
  if (!VALID_PLAN_IDS.has(candidate)) return null;
  if (candidate === PLAN_ID.marriage_report && !allowMarriageReport) return null;
  return buildPick(candidate, reason, undefined, language);
}

/**
 * Build a report pick for an explicit plan id chosen by the recommendation
 * engine (ADR-0024). Returns null when the id is not a known SEO report plan
 * (e.g. the engine surfaced a product SKU at a non-report placement), so the
 * caller can fall back to {@link pickPrimaryReport} and always render an offer.
 */
export function pickForPlanId(planId: string, language = "en"): PrimaryReportPick | null {
  if (!VALID_PLAN_IDS.has(planId as SEOReportPlanId)) return null;
  return buildPick(planId as SEOReportPlanId, "next_report_param", undefined, language);
}

/**
 * The chat reading value surface is a proven paid-report checkout surface.
 * Authoritative recommendations may personalize the report plan, but non-report
 * product/bundle picks must not replace the report CTA on this surface.
 */
export function pickChatReadingReportOffer({
  engineResult,
  matcherPrimary,
  language = "en",
}: {
  engineResult: RecommendResult | null;
  matcherPrimary: PrimaryReportPick;
  language?: string;
}): PrimaryReportPick {
  if (!engineResult?.authoritative || !engineResult.offerId) {
    return matcherPrimary;
  }
  if (engineResult.offerType && engineResult.offerType !== "report") {
    return matcherPrimary;
  }
  return pickForPlanId(engineResult.offerId, language) ?? matcherPrimary;
}

export const SECONDARY_PLAN_IDS: SEOReportPlanId[] = [
  PLAN_ID.brihat_kundali,
  PLAN_ID.marriage_report,
  PLAN_ID.career_report,
  PLAN_ID.annual_forecast,
  PLAN_ID.mahadasha_forecast,
  PLAN_ID.sade_sati,
  PLAN_ID.pitra_dosh,
  PLAN_ID.muhurat_report,
  PLAN_ID.dosha_remedy_plan,
];

export function getSecondaryPlans(primaryPlanId: SEOReportPlanId) {
  return SECONDARY_PLAN_IDS.filter(
    (id) => id !== primaryPlanId && id !== PLAN_ID.marriage_report
  ).map((id) => ({
    planId: id,
    ...PLAN_DEFAULTS[id],
  }));
}
